import React, { useRef, useState } from 'react';
import { Volume2, VolumeX, Play, Pause, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { BRAND, MARQUEE_ITEMS } from '../data/mock';
import { useQuote } from '../context/QuoteContext';

// Poster image (Indian wedding / celebration vibe)
const HERO_POSTER = 'https://images.unsplash.com/photo-1583939003579-730e3918a45a?auto=format&fit=crop&w=1900&q=80';

const Hero = () => {
  const { openQuote } = useQuote();
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  const toggleSound = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) { videoRef.current.play(); setPlaying(true); }
    else { videoRef.current.pause(); setPlaying(false); }
  };

  return (
    <section id="home" className="relative pt-28 md:pt-32">
      {/* Marquee ribbon on blush */}
      <div className="relative overflow-hidden bg-blush border-y border-pink-100">
        <div className="flex whitespace-nowrap animate-marquee py-2.5">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i} className="mx-4 sm:mx-6 inline-flex items-center gap-2 text-[13px] font-medium text-pink-brand">
              <span className="w-1.5 h-1.5 rounded-full bg-pink-brand/60" />
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* Video hero */}
      <div className="relative w-full">
        <div className="relative w-full aspect-[16/8] sm:aspect-[21/9] max-h-[78vh] overflow-hidden bg-blush">
          {/* Background image (always visible) */}
          <img
            src={HERO_POSTER}
            alt="Mr. Coconetto"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Video overlay on top (hides itself if fails) */}
          <video
            ref={videoRef}
            src={BRAND.heroVideo}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            data-testid="hero-video"
          />
          {/* Soft blush wash + dark left gradient for text contrast */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/15 to-black/10 pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-t from-pink-brand/20 to-transparent pointer-events-none" />

          {/* Top-left tagline */}
          <div className="hidden md:block absolute top-10 left-10 max-w-lg text-white drop-shadow-[0_4px_12px_rgba(0,0,0,0.4)]">
            <div className="font-script text-2xl text-pink-100">Simply Original</div>
            <div className="font-display font-bold text-4xl lg:text-5xl mt-1 leading-[1.1]">
              Makes Every <em className="font-script not-italic text-pink-200">Occasion</em>
              <br />Extra Special
            </div>
          </div>

          {/* Bottom-right controls + A+ badge */}
          <div className="absolute bottom-24 sm:bottom-28 md:bottom-32 right-4 sm:right-8 flex items-end gap-3 sm:gap-5">
            {/* 100% A+ Grade circle */}
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-pink-brand to-pink-600 text-white flex flex-col items-center justify-center shadow-pink-glow animate-float">
              <div className="font-display font-black text-lg sm:text-xl leading-none">100%</div>
              <div className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider mt-1">A+ Grade</div>
            </div>
          </div>
        </div>

        {/* CTA row (below video) */}
        <div className="relative -mt-8 md:-mt-12 z-10 max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-3xl shadow-floating border border-pink-100 p-5 md:p-7 flex flex-col md:flex-row items-center gap-5 md:gap-8">
            <div className="flex-1 text-center md:text-left">
              <div className="font-script text-pink-brand text-lg">Sip · Smile · Celebrate</div>
              <div className="font-display font-bold text-2xl md:text-3xl text-stone-900 mt-1 leading-tight">
                India's <em className="font-script not-italic text-pink-brand">#1</em> Custom Coconut Brand
              </div>
              <div className="text-sm text-stone-600 mt-2 max-w-lg">
                Personalized, AI-engraved, A+ grade tender coconuts for weddings, corporate events & every big day.
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap justify-center md:justify-end">
              <Button
                onClick={() => openQuote({ source: 'hero-primary' })}
                className="bg-pink-brand hover:bg-[#BE185D] text-white rounded-full h-12 px-7 font-semibold shadow-pink-glow"
                data-testid="hero-check-availability-button"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Check Availability
              </Button>
              <a
                href="#customize"
                className="inline-flex items-center gap-2 rounded-full h-12 px-6 bg-stone-900 hover:bg-stone-800 text-white font-semibold text-sm shadow-md"
                data-testid="hero-customize-link"
              >
                Customize Your Coconut →
              </a>
              <a
                href={`tel:${BRAND.phone}`}
                className="inline-flex items-center gap-2 rounded-full h-12 px-6 border border-pink-200 text-pink-brand bg-white hover:bg-pink-50 font-semibold text-sm"
                data-testid="hero-call-now-link"
              >
                Call {BRAND.phone}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
